import { Component, EventEmitter, Input, Output } from "@angular/core";
import { NavController, NavParams, ViewController } from "ionic-angular";
import { Order, OrderComponent } from "models/order-management/models";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

@Component({
  selector: "reason-for-respite",
  templateUrl: "reason-for-respite.html",
})
export class ReasonForRespiteComponent implements OrderComponentInterface {
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() set orderFormState(orderFormState: OrderFormState) {
    this._orderFormState = orderFormState;
    this.isLevelOfCare = orderFormState.levelOfCare === "respiteCare";
    const componentField = this.orderComponent.properties[0].name;
    if (!this.isLevelOfCare) {
      this.reasonForRespiteValue = null;
      this.orderCreateRequestEvent.emit({
        deletes: [componentField as keyof Order],
      });
    }
    this.checkIsValid();
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  _orderFormState: OrderFormState;
  reasonForRespiteValue: string = "";
  isLevelOfCare = false;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateData: StateManagementService
  ) {}

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    this._orderFormState.formType === "amendment"
      ? this.setAmendedValue()
      : null;
    this.checkIsValid();
  }

  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  setAmendedValue() {
    const componentField: string = this.orderComponent.properties[0].name;
    this.reasonForRespiteValue = this.orderCreateRequest[componentField];
    this.checkIsValid();
  }

  /****************************************************************************
   * Component Related Methods                                                *
   ****************************************************************************/
  onReasonForRespiteValueChange(event: string) {
    if (event) {
      if (
        this.orderComponent.properties[0].amendable &&
        this._orderFormState.formType === "amendment"
      ) {
        const componentField = this.orderComponent.properties[0].name;
        const prevValue = this.orderCreateRequest[componentField].toString();
        if (prevValue !== event) {
          const amendedObject: OrderComponentPropertyUpdate = { updates: {} };
          amendedObject.updates = {
            propertyName: componentField,
            previousValueDisplay: this.orderCreateRequest[componentField],
            previousValue: this.orderCreateRequest[componentField],
            newValue: event,
          };
          this.amendedOrderRequestEvent.emit(amendedObject);
        } else {
          const amendedObject: OrderComponentPropertyUpdate = {
            updates: {},
            deletes: [componentField as keyof Order],
          };
          this.amendedOrderRequestEvent.emit(amendedObject);
        }
      } else {
        const updateObject: OrderCreateRequestUpdate = { updates: {} };
        const componentField = this.orderComponent.properties[0].name;
        updateObject.updates[componentField] = event;
        this.orderCreateRequestEvent.emit(updateObject);
      }
    }
    this.checkIsValid();
  }

  checkIsValid() {
    if (this.isLevelOfCare) {
      if (this.reasonForRespiteValue && this.reasonForRespiteValue.length > 0) {
        this.isValidEvent.emit(true);
      } else {
        this.isValidEvent.emit(false);
      }
    } else {
      this.isValidEvent.emit(true);
    }
  }

  isRequired() {
    return this._orderFormState.levelOfCare === "respiteCare";
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "")
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .replace(/[\s_]+/g, "-")
        .toLowerCase();
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/reason-for-respite/reason-for-respite.ts